import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AndroidBluetoothMidi,
  canSearchForBluetoothKeyboards,
  connectPairedBluetoothKeyboard,
  initialBluetoothKeyboardStep,
  isAndroidBluetoothMidiAvailable,
  reconnectSavedBluetoothKeyboard,
  savedBluetoothKeyboard,
  searchBluetoothKeyboards,
  stepAfterConnectionChange,
} from 'Utils/AndroidBluetoothMidi';
import type {
  BluetoothKeyboardSearch,
  BluetoothKeyboardStep,
  PairedBluetoothKeyboard,
  SavedBluetoothKeyboard,
} from 'Utils/AndroidBluetoothMidi';
import { MIDI_PROMPT_PLACEHOLDER, midiPromptMessage } from 'Utils/midiPromptMessage';
import type { MidiPromptBluetoothAction } from 'Utils/midiPromptMessage';
import { usePressedState } from 'Utils/usePressedState';

// MUS-450 - Settings > Keyboard on Android: connect a Bluetooth MIDI keyboard. On Android
// 12+ the app searches for it, so it need not be paired first; on older Android it lists the
// keyboards paired in Android's Bluetooth settings. Renders nothing on iOS and the web.
// The MIDI prompt's Android Bluetooth button uses the same steps and copy.

const headingSx = {
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  letterSpacing: '0.01em',
  color: '#ECECEC',
  marginBottom: '10px',
};

const messageSx = {
  fontSize: '14px',
  lineHeight: '20px',
  color: 'rgba(236,236,236,0.8)',
  marginBottom: '10px',
};

const primaryButtonSx = {
  px: '20px',
  py: '10px',
  borderRadius: '10px',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: '#4A90D9',
  color: '#FFFFFF',
  fontSize: '15px',
  fontWeight: 'bold',
  fontFamily: 'Lato',
  '&:hover': { opacity: 0.85 },
};

const secondaryButtonSx = {
  ...primaryButtonSx,
  backgroundColor: 'transparent',
  border: '1px solid rgba(255,255,255,0.28)',
  color: '#ECECEC',
};

const disabledButtonSx = {
  opacity: 0.5,
  cursor: 'default',
  '&:hover': { opacity: 0.5 },
};

type ActionButtonProps = {
  label: string;
  onPress: () => void;
  secondary?: boolean;
  disabled?: boolean;
};

const ActionButton = ({
  label,
  onPress,
  secondary = false,
  disabled = false,
}: ActionButtonProps) => {
  const { pressedSx, pressHandlers } = usePressedState();
  return (
    <Box
      component="button"
      type="button"
      onClick={onPress}
      disabled={disabled}
      {...(disabled ? {} : pressHandlers)}
      sx={{
        ...(secondary ? secondaryButtonSx : primaryButtonSx),
        ...(disabled ? disabledButtonSx : pressedSx),
      }}
    >
      {label}
    </Box>
  );
};

const secondsUntil = (retryAt: number, now: number) => Math.ceil((retryAt - now) / 1000);

/**
 * Search again after Android refused a search for starting too often: the button counts
 * down the wait and works once it is over, so it explains itself.
 */
const SearchAgainAfterWait = ({ retryAt, onSearch }: { retryAt: number; onSearch: () => void }) => {
  const [now, setNow] = useState(() => Date.now());
  const waiting = now < retryAt;

  useEffect(() => {
    if (!waiting) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [waiting]);

  return (
    <ActionButton
      label={waiting ? `Search again in ${secondsUntil(retryAt, now)} s` : 'Search again'}
      onPress={onSearch}
      disabled={waiting}
    />
  );
};

const Message = ({ children }: { children: ReactNode }) => (
  <Box sx={messageSx} role="status">
    {children}
  </Box>
);

const ButtonRow = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>{children}</Box>
);

const SpacedButtonRow = ({ children }: { children: ReactNode }) => (
  <Box sx={{ marginTop: '10px' }}>
    <ButtonRow>{children}</ButtonRow>
  </Box>
);

type KeyboardButtonsProps = {
  keyboards: PairedBluetoothKeyboard[];
  onChooseKeyboard: (keyboard: PairedBluetoothKeyboard) => void;
};

const KeyboardButtons = ({ keyboards, onChooseKeyboard }: KeyboardButtonsProps) => (
  <ButtonRow>
    {keyboards.map((keyboard) => (
      <ActionButton
        key={keyboard.id}
        label={keyboard.name}
        onPress={() => onChooseKeyboard(keyboard)}
        secondary={!keyboard.midi}
      />
    ))}
  </ButtonRow>
);

type BluetoothKeyboardStepsProps = {
  step: BluetoothKeyboardStep;
  /** The main button while nothing is happening: connect, or reconnect the saved keyboard. */
  connectLabel: string;
  onConnect: () => void;
  onTryAgain: () => void;
  onChooseKeyboard: (keyboard: PairedBluetoothKeyboard) => void;
  onDisconnect: () => void;
  /** Search again after a search ended. */
  onSearch: () => void;
  onStopSearch: () => void;
  /**
   * What Settings says before a connect: turn the keyboard on (the app searches), pair it
   * first (Android 11 and older, or an app build without the search), or nothing (the
   * MIDI prompt has its own copy).
   */
  idleHint: IdleHint;
  /**
   * After a failed connect, also offer a search: Try again retries the same keyboard, which
   * may be on a new address.
   */
  offerSearchAfterFailedConnect: boolean;
};

type IdleHint = 'turn-on' | 'pair-first' | 'none';

/** The connect steps, shared by Settings > Keyboard and the MIDI prompt. */
const BluetoothKeyboardSteps = ({
  step,
  connectLabel,
  onConnect,
  onTryAgain,
  onChooseKeyboard,
  onDisconnect,
  onSearch,
  onStopSearch,
  idleHint,
  offerSearchAfterFailedConnect,
}: BluetoothKeyboardStepsProps) => {
  const openBluetoothSettings = () => {
    void AndroidBluetoothMidi.openBluetoothSettings().catch(() => undefined);
  };

  const openAppSettings = () => {
    void AndroidBluetoothMidi.openAppSettings().catch(() => undefined);
  };

  const tryAgain = <ActionButton label="Try again" onPress={onTryAgain} />;

  switch (step.kind) {
    case 'checking':
      return <Message>Looking for your keyboard...</Message>;
    case 'unsupported':
      return <Message>This device can&apos;t connect to Bluetooth MIDI keyboards.</Message>;
    case 'bluetooth-off':
      return (
        <>
          <Message>Bluetooth is off. Turn it on, then try again.</Message>
          <ButtonRow>
            {tryAgain}
            <ActionButton
              label="Open Bluetooth settings"
              onPress={openBluetoothSettings}
              secondary
            />
          </ButtonRow>
        </>
      );
    case 'permission-denied':
      return (
        <>
          <Message>
            MuseFlow needs the Nearby devices permission to connect to a Bluetooth keyboard. A USB
            keyboard works without it. If Android doesn&apos;t ask again, open MuseFlow&apos;s
            settings and allow Nearby devices.
          </Message>
          <ButtonRow>
            {tryAgain}
            <ActionButton label="Open app settings" onPress={openAppSettings} secondary />
          </ButtonRow>
        </>
      );
    case 'no-keyboards':
      return step.searched ? (
        <>
          <Message>No keyboard found. Turn it on and keep it close, then Search again.</Message>
          {step.heardNothing && step.retryAt === undefined && (
            <Message>
              If you searched several times just now, wait half a minute, then Search again.
            </Message>
          )}
          <Message>Still not found? Pair it in Android&apos;s Bluetooth settings.</Message>
          <ButtonRow>
            {step.retryAt === undefined ? (
              <ActionButton label="Search again" onPress={onSearch} />
            ) : (
              <SearchAgainAfterWait key={step.retryAt} retryAt={step.retryAt} onSearch={onSearch} />
            )}
            <ActionButton
              label="Open Bluetooth settings"
              onPress={openBluetoothSettings}
              secondary
            />
          </ButtonRow>
        </>
      ) : (
        <>
          <Message>
            No paired Bluetooth keyboard found. Pair your keyboard in Android&apos;s Bluetooth
            settings, then try again.
          </Message>
          <ButtonRow>
            {tryAgain}
            <ActionButton
              label="Open Bluetooth settings"
              onPress={openBluetoothSettings}
              secondary
            />
          </ButtonRow>
        </>
      );
    case 'searching':
      return (
        <>
          <Message>Searching for keyboards...</Message>
          {step.keyboards.length > 0 && (
            <KeyboardButtons keyboards={step.keyboards} onChooseKeyboard={onChooseKeyboard} />
          )}
          <SpacedButtonRow>
            <ActionButton label="Stop" onPress={onStopSearch} secondary />
          </SpacedButtonRow>
        </>
      );
    case 'choose':
      return (
        <>
          <Message>Choose your keyboard:</Message>
          <KeyboardButtons keyboards={step.keyboards} onChooseKeyboard={onChooseKeyboard} />
          {step.searched && (
            <SpacedButtonRow>
              <ActionButton label="Search again" onPress={onSearch} secondary />
            </SpacedButtonRow>
          )}
        </>
      );
    case 'connecting':
      return <Message>Connecting to {step.name}...</Message>;
    case 'connected':
      return (
        <>
          <Message>Connected to {step.name}.</Message>
          <ActionButton label="Disconnect" onPress={onDisconnect} secondary />
        </>
      );
    case 'failed':
      return (
        <>
          <Message>
            Couldn&apos;t connect to {step.name}. Make sure it is on and nearby, then try again.
          </Message>
          {offerSearchAfterFailedConnect ? (
            <ButtonRow>
              {tryAgain}
              <ActionButton label="Search for keyboards" onPress={onSearch} secondary />
            </ButtonRow>
          ) : (
            tryAgain
          )}
        </>
      );
    case 'search-failed': {
      const pairedKeyboards = step.keyboards ?? [];
      const offerPaired = pairedKeyboards.length > 0 && (
        <>
          <Message>Or choose a paired keyboard:</Message>
          <KeyboardButtons keyboards={pairedKeyboards} onChooseKeyboard={onChooseKeyboard} />
        </>
      );
      return step.retryAt === undefined ? (
        <>
          <Message>Couldn&apos;t search. Wait a few seconds, then Search again.</Message>
          <Message>If it keeps failing, turn Bluetooth off and on again.</Message>
          <ButtonRow>
            <ActionButton label="Search again" onPress={onSearch} />
            <ActionButton
              label="Open Bluetooth settings"
              onPress={openBluetoothSettings}
              secondary
            />
          </ButtonRow>
          {offerPaired && <Box sx={{ marginTop: '10px' }}>{offerPaired}</Box>}
        </>
      ) : (
        <>
          <Message>
            Android allows only a few searches in a short time. Wait a moment, then Search again.
          </Message>
          <Message>Make sure your keyboard is on and close.</Message>
          <SearchAgainAfterWait key={step.retryAt} retryAt={step.retryAt} onSearch={onSearch} />
          {offerPaired && <Box sx={{ marginTop: '10px' }}>{offerPaired}</Box>}
        </>
      );
    }
    case 'lost':
    case 'idle':
    case 'error':
      return (
        <>
          {step.kind === 'lost' && <Message>Lost the connection to {step.name}.</Message>}
          {step.kind === 'error' && <Message>Something went wrong. Try again.</Message>}
          {step.kind === 'idle' && idleHint === 'turn-on' && (
            <Message>
              Turn on your Bluetooth MIDI keyboard and keep it close, then connect it here.
            </Message>
          )}
          {step.kind === 'idle' && idleHint === 'pair-first' && (
            <Message>
              Pair your Bluetooth MIDI keyboard in Android&apos;s Bluetooth settings first, then
              connect it here.
            </Message>
          )}
          <ActionButton label={connectLabel} onPress={onConnect} />
        </>
      );
  }
};

/**
 * The steps of one picker. `runStep` shows `pending` first, then the step the action ends
 * on. `findKeyboards` runs a search whose results stream in; a search ends when the user
 * stops it, chooses a keyboard, a keyboard connects, or the picker closes.
 */
const useBluetoothKeyboardStep = () => {
  const [step, setStep] = useState<BluetoothKeyboardStep>({ kind: 'idle' });
  const searchRef = useRef<BluetoothKeyboardSearch | null>(null);
  // Bumped whenever a search is ended, so a late result of an old search is dropped.
  const searchGenerationRef = useRef(0);

  const endSearch = useCallback(() => {
    searchGenerationRef.current += 1;
    searchRef.current?.cancel();
    searchRef.current = null;
  }, []);

  useEffect(() => endSearch, [endSearch]);

  const runStep = useCallback(
    async (pending: BluetoothKeyboardStep, action: () => Promise<BluetoothKeyboardStep>) => {
      endSearch();
      setStep(pending);
      setStep(await action());
    },
    [endSearch],
  );

  const findKeyboards = useCallback(() => {
    endSearch();
    const generation = searchGenerationRef.current;
    const isCurrent = () => searchGenerationRef.current === generation;
    setStep({ kind: 'checking' });
    void searchBluetoothKeyboards((next) => {
      if (isCurrent()) setStep(next);
    }).then((search) => {
      if (search && isCurrent()) searchRef.current = search;
      else search?.cancel();
    });
  }, [endSearch]);

  const stopSearch = useCallback(() => {
    const search = searchRef.current;
    searchRef.current = null;
    search?.stop();
  }, []);

  const chooseKeyboard = useCallback(
    (keyboard: PairedBluetoothKeyboard) => {
      void runStep({ kind: 'connecting', name: keyboard.name }, () =>
        connectPairedBluetoothKeyboard(keyboard),
      );
    },
    [runStep],
  );

  const disconnectKeyboard = useCallback(async () => {
    endSearch();
    try {
      await AndroidBluetoothMidi.disconnect();
    } finally {
      setStep({ kind: 'idle' });
    }
  }, [endSearch]);

  return {
    step,
    setStep,
    runStep,
    findKeyboards,
    stopSearch,
    endSearch,
    chooseKeyboard,
    disconnectKeyboard,
  };
};

export const AndroidBluetoothKeyboardPanel = () => {
  const {
    step,
    setStep,
    findKeyboards,
    stopSearch,
    endSearch,
    chooseKeyboard,
    disconnectKeyboard,
  } = useBluetoothKeyboardStep();
  const [canSearch, setCanSearch] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    void initialBluetoothKeyboardStep().then((initialStep) => {
      if (active) setStep((current) => (current.kind === 'idle' ? initialStep : current));
    });
    void canSearchForBluetoothKeyboards().then((searches) => {
      if (active) setCanSearch(searches);
    });
    const listenerHandle = AndroidBluetoothMidi.addListener('connectionChanged', (change) => {
      if (!active) return;
      // The saved keyboard came back by itself: the search has nothing left to offer.
      if (change.connected) endSearch();
      setStep((current) => stepAfterConnectionChange(current, change));
    });
    return () => {
      active = false;
      void listenerHandle.then((handle) => handle.remove());
    };
  }, [setStep, endSearch]);

  return (
    <Box sx={{ marginBottom: '24px', fontFamily: 'Lato' }} data-testid="android-bluetooth-keyboard">
      <Box sx={headingSx}>Bluetooth keyboard</Box>
      <BluetoothKeyboardSteps
        step={step}
        connectLabel="Connect Bluetooth keyboard"
        onConnect={findKeyboards}
        onTryAgain={findKeyboards}
        onChooseKeyboard={chooseKeyboard}
        onDisconnect={() => {
          void disconnectKeyboard();
        }}
        onSearch={findKeyboards}
        onStopSearch={stopSearch}
        idleHint={canSearch === null ? 'none' : canSearch ? 'turn-on' : 'pair-first'}
        offerSearchAfterFailedConnect={false}
      />
    </Box>
  );
};

/** How long the MIDI prompt waits for the saved keyboard before it shows its copy without it. */
export const PROMPT_STATUS_WAIT_MS = 1000;

type AndroidBluetoothMidiPromptProps = {
  /** The style of the prompt's own copy, so the message matches it. */
  messageSx: SxProps<Theme>;
};

/**
 * MUS-450 - the Android MIDI prompt's copy and Bluetooth button: "Reconnect <name>" for the
 * keyboard the user connected last (the app also retries it by itself), otherwise the
 * Settings connect steps. A connected keyboard closes the prompt, as a USB one does.
 */
export const AndroidBluetoothMidiPrompt = ({ messageSx }: AndroidBluetoothMidiPromptProps) => {
  const { step, runStep, findKeyboards, stopSearch, chooseKeyboard, disconnectKeyboard } =
    useBluetoothKeyboardStep();
  const [saved, setSaved] = useState<SavedBluetoothKeyboard | null | undefined>(undefined);
  // The copy names the button, which depends on the saved keyboard. It is chosen once, so it
  // never changes in front of the user: when the status arrives, or after a short wait
  // without the button's name if the status call hangs. Until then a neutral line shows.
  const [copyAction, setCopyAction] = useState<MidiPromptBluetoothAction | null>(null);

  useEffect(() => {
    let active = true;
    const chooseCopyOnce = (action: MidiPromptBluetoothAction) =>
      setCopyAction((chosen) => chosen ?? action);
    const withoutStatus = setTimeout(() => {
      if (active) chooseCopyOnce('none');
    }, PROMPT_STATUS_WAIT_MS);
    void savedBluetoothKeyboard().then((keyboard) => {
      clearTimeout(withoutStatus);
      if (!active) return;
      setSaved(keyboard);
      chooseCopyOnce(keyboard ? 'reconnect' : 'connect');
    });
    return () => {
      active = false;
      clearTimeout(withoutStatus);
    };
  }, []);

  // The keyboard the user picked from a search here, if any. A failed connect names that
  // keyboard, so Try again retries it, not the saved one the user moved away from.
  const pickedRef = useRef<PairedBluetoothKeyboard | null>(null);

  const reconnect = (keyboard: SavedBluetoothKeyboard) => {
    void runStep({ kind: 'connecting', name: keyboard.name }, () =>
      reconnectSavedBluetoothKeyboard(keyboard),
    );
  };

  const connect = () => {
    if (saved) reconnect(saved);
    else findKeyboards();
  };

  const tryAgain = () => {
    const keyboard = pickedRef.current ?? saved;
    if (keyboard) reconnect(keyboard);
    else findKeyboards();
  };

  const choose = (keyboard: PairedBluetoothKeyboard) => {
    pickedRef.current = keyboard;
    chooseKeyboard(keyboard);
  };

  return (
    <>
      <Box sx={messageSx}>
        {copyAction === null ? MIDI_PROMPT_PLACEHOLDER : midiPromptMessage(true, copyAction)}
      </Box>
      {saved !== undefined && (
        <Box
          sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          data-testid="android-bluetooth-midi-prompt"
        >
          <BluetoothKeyboardSteps
            step={step}
            connectLabel={saved ? `Reconnect ${saved.name}` : 'Connect Bluetooth keyboard'}
            onConnect={connect}
            onTryAgain={tryAgain}
            onChooseKeyboard={choose}
            onDisconnect={() => {
              void disconnectKeyboard();
            }}
            onSearch={findKeyboards}
            onStopSearch={stopSearch}
            idleHint="none"
            offerSearchAfterFailedConnect
          />
        </Box>
      )}
    </>
  );
};

const AndroidBluetoothKeyboard = () =>
  isAndroidBluetoothMidiAvailable() ? <AndroidBluetoothKeyboardPanel /> : null;

export default AndroidBluetoothKeyboard;
